// =============================================================================
// src/data/sentinel-client.ts — Cliente Hasura anon para dados Sentinel.
// =============================================================================

import { postHasuraGraphql } from './hasura-graphql-post';
import {
  SENTINEL_DASHBOARD_QUERY,
  type SentinelAlertRow,
  type SentinelActionRecommendationRow,
  type SentinelAmplifierRow,
  type SentinelCrisisMetricsRow,
  type SentinelFeedRow,
  type SentinelGraphqlData,
  type SentinelLifecycleRow,
  type SentinelOriginRow,
  type SentinelReputationRow,
  type SentinelSentimentRow,
  type SentinelTrendRow,
  type SentinelVolumeHourRow,
} from './sentinel-mapping-query';

export interface SentinelDashboard {
  readonly sentiment: SentinelSentimentRow | null;
  readonly feed: readonly SentinelFeedRow[];
  readonly reputation: SentinelReputationRow | null;
  readonly alerts: readonly SentinelAlertRow[];
  readonly trend: readonly SentinelTrendRow[];
  readonly actionRecommendations: readonly SentinelActionRecommendationRow[];
  // (reputationDelta/netSentimentDelta/dailyBriefing REMOVIDOS 2026-07-28: só o
  // buildBriefingPatch consumia, e o briefing migrou para o social_listening.)
  // Onda 2 da Crise — views por tópico (migration 041). Opcionais (fixtures legados). Alimentam buildCrisisLivePatch.
  readonly crisisMetrics?: readonly SentinelCrisisMetricsRow[];
  readonly volumeHourly?: readonly SentinelVolumeHourRow[];
  readonly amplifiers?: readonly SentinelAmplifierRow[];
  readonly origin?: readonly SentinelOriginRow[];
  readonly lifecycle?: readonly SentinelLifecycleRow[];
}

export interface SentinelClientDeps {
  readonly endpoint: string;
  readonly clientId: string;
  readonly apiUrl?: string;
  readonly fetchImpl?: typeof fetch;
  readonly timeoutMs?: number;
}

export interface SentinelClient {
  readonly fetchDashboard: () => Promise<SentinelDashboard>;
  readonly fetchRadarSummary: () => Promise<Record<string, unknown>>;
}

const DEFAULT_TIMEOUT_MS = 8000;

export function createSentinelClient(deps: SentinelClientDeps): SentinelClient {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const timeoutMs = deps.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const apiUrl = (deps.apiUrl ?? 'http://localhost:8000').replace(/\/$/, '');

  return {
    fetchDashboard: () => fetchDashboard(deps.endpoint, deps.clientId, fetchImpl, timeoutMs),
    fetchRadarSummary: () =>
      fetchRadarSummary(`${apiUrl}/radar/client/${deps.clientId}/summary`, fetchImpl, timeoutMs),
  };
}

async function fetchDashboard(
  endpoint: string,
  clientId: string,
  fetchImpl: typeof fetch,
  timeoutMs: number,
): Promise<SentinelDashboard> {
  // Transporte (POST + header x-hasura-client-id + timeout) extraído para
  // hasura-graphql-post.ts — compartilhado com o Analytics live (DRY 2026-07).
  const data = await postHasuraGraphql<SentinelGraphqlData>({
    endpoint,
    clientId,
    query: SENTINEL_DASHBOARD_QUERY,
    variables: { clientId },
    fetchImpl,
    timeoutMs,
    errorLabel: 'Sentinel',
  });
  return {
    sentiment: data.monitoring_v_sentiment_24h[0] ?? null,
    feed: data.monitoring_v_feed_recent,
    reputation: data.monitoring_v_reputation_summary[0] ?? null,
    alerts: data.monitoring_alert,
    trend: data.monitoring_v_reputation_trend,
    actionRecommendations: data.gold_ai_action_recommendation ?? [],
    crisisMetrics: data.monitoring_v_crisis_metrics_24h,
    volumeHourly: data.monitoring_v_content_volume_hourly,
    amplifiers: data.monitoring_v_topic_amplifiers,
    origin: data.monitoring_v_topic_origin,
    lifecycle: data.monitoring_v_crisis_lifecycle_phase,
  };
}

async function fetchRadarSummary(
  url: string,
  fetchImpl: typeof fetch,
  timeoutMs: number,
): Promise<Record<string, unknown>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetchImpl(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`Radar ${url}: HTTP ${res.status}`);
    return (await res.json()) as Record<string, unknown>;
  } finally {
    clearTimeout(timer);
  }
}

export type { SentinelGraphqlData };
